/**
 * The finance tools the ledger, payouts and audit screens were missing (queue item 15).
 *
 * - **JournalExport** (admin doc 15 §4) — the journal as CSV for the CA, one row per leg. Over the
 *   server's row cap it is refused rather than truncated, and the refusal is shown as it came.
 * - **AddAccount / AccountEditor** (doc 15 §5) — the chart is add-only. Code, name, type and side
 *   are frozen at creation; after that only the description and the active flag move, with a
 *   reason. An account added here exists in this environment until the seed catches up.
 * - **BankAccounts** (doc 16 §6) — a provider's destinations, and the manual check for when the
 *   penny drop is down. The reason is the record of *how* somebody checked.
 * - **AuditTrail** (doc 28 §3) — everything that happened to one row, newest first.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useAddAccount,
  useExportJournal,
  useUpdateAccount,
  type AccountRow,
} from '../lib/api/hooks/ledger';
import { useBankAccounts, useVerifyBankAccount } from '../lib/api/hooks/payouts';
import { AuditTrailPanel as AuditTrail } from '../components/audit';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/* ──────────────────────────────  Journal export  ────────────────────────────── */

export function JournalExport() {
  const { scope, toast } = useConsole();
  const exportJournal = useExportJournal();
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const sinceError = since === '' || DAY.test(since) ? undefined : 'YYYY-MM-DD';
  const untilError =
    until === '' || !DAY.test(until)
      ? until === ''
        ? undefined
        : 'YYYY-MM-DD'
      : DAY.test(since) && until < since
        ? 'Not before the start'
        : undefined;
  const ready = DAY.test(since) && DAY.test(until) && !untilError;

  const run = () =>
    exportJournal.mutate(
      // The whole of both days: the range a CA asks for is inclusive at each end.
      { since: `${since}T00:00:00`, until: `${until}T23:59:59` },
      {
        onSuccess: (csv) => {
          const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `journal-${since}-${until}.csv`;
          link.click();
          URL.revokeObjectURL(url);
          toast({ tone: 'success', message: 'Journal exported' });
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section title="Export for the accountant" note="CSV, one row per leg · every export is audited">
      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
        <Input label="From" placeholder="YYYY-MM-DD" value={since} onChange={setSince} error={sinceError} />
        <Input label="To" placeholder="YYYY-MM-DD" value={until} onChange={setUntil} error={untilError} />
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          variant="secondary"
          disabled={!ready || exportJournal.isPending}
          onClick={run}
        >
          {exportJournal.isPending ? 'Exporting…' : 'Export CSV'}
        </ScopeAction>
      </div>
    </Section>
  );
}

/* ──────────────────────────────  Chart of accounts  ────────────────────────────── */

/* `equity` is reserved and unused: this is the operational subledger, not the statutory books. */
const ACCOUNT_TYPES = ['asset', 'liability', 'revenue', 'expense'] as const;
const SIDES = ['debit', 'credit'] as const;

export function AddAccount() {
  const { scope, toast } = useConsole();
  const add = useAddAccount();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [side, setSide] = useState('');
  const [description, setDescription] = useState('');

  const codeError = code === '' || /^[1245]\d{3}$/.test(code) ? undefined : 'Four digits, starting 1, 2, 4 or 5';
  const nameError =
    name === '' || /^[a-z][a-z0-9_]{2,47}$/.test(name) ? undefined : 'lower_snake_case, 3–48 characters';
  const ready =
    /^[1245]\d{3}$/.test(code) &&
    /^[a-z][a-z0-9_]{2,47}$/.test(name) &&
    type !== '' &&
    side !== '' &&
    description.trim().length >= 3;

  const submit = () =>
    add.mutate(
      { code, name, account_type: type, normal_side: side, description: description.trim() },
      {
        onSuccess: (account) => {
          toast({ tone: 'success', message: `${account.code} ${account.name} added` });
          setCode('');
          setName('');
          setType('');
          setSide('');
          setDescription('');
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section
      title="Add an account"
      note="add-only · it exists in this environment until the seed carries it to every other"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-md)' }}>
        <Input label="Code" inputMode="numeric" value={code} onChange={setCode} error={codeError} />
        <Input label="Name" value={name} onChange={setName} error={nameError} />
        <Select label="Type" placeholder="Choose" options={ACCOUNT_TYPES} value={type} onChange={setType} />
        <Select label="Increases on" placeholder="Choose" options={SIDES} value={side} onChange={setSide} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', marginTop: 'var(--sp-md)' }}>
        <Input label="What it holds" value={description} onChange={setDescription} style={{ flex: 1 }} />
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          variant="secondary"
          disabled={!ready || add.isPending}
          onClick={submit}
        >
          Add account
        </ScopeAction>
      </div>
    </Section>
  );
}

export function AccountEditor({ account, onClose }: { account: AccountRow; onClose: () => void }) {
  const { scope, toast } = useConsole();
  const update = useUpdateAccount();
  const [description, setDescription] = useState(account.description);
  const [active, setActive] = useState(account.is_active);
  const [reason, setReason] = useState('');

  const describedChange = description.trim() !== account.description;
  const activeChange = active !== account.is_active;
  const ready =
    (describedChange || activeChange) && description.trim().length >= 3 && reason.trim().length >= 3;

  const save = () =>
    update.mutate(
      {
        id: account.id,
        reason: reason.trim(),
        // Only what moved: an unchanged field sent anyway is a change in the audit diff that wasn't.
        ...(describedChange ? { description: description.trim() } : {}),
        ...(activeChange ? { is_active: active } : {}),
      },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${account.code} saved` });
          onClose();
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <>
      <Section
        title={`${account.code} ${account.name}`}
        note="code, name, type and side never change — every past entry means what the account meant then"
        actions={
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        }
      >
        <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
          <Input label="What it holds" value={description} onChange={setDescription} />
          <Toggle
            label="Open for postings"
            on={active}
            onChange={setActive}
          />
          <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
            {active ? 'Open for postings' : 'Retired — its history stays queryable under the same code'}
          </span>
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end' }}>
            <Input label="Why" value={reason} onChange={setReason} style={{ flex: 1 }} />
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              disabled={!ready || update.isPending}
              onClick={save}
            >
              Save
            </ScopeAction>
          </div>
        </div>
      </Section>
      <AuditTrail entityType="ledger_account" entityId={account.id} />
    </>
  );
}

/* ──────────────────────────────  Bank accounts  ────────────────────────────── */

const PAYABLE_TO = new Set(['verified', 'manually_verified']);

export function BankAccounts({ panditProfileId }: { panditProfileId: number }) {
  const { scope, toast } = useConsole();
  const accounts = useBankAccounts(panditProfileId);
  const verify = useVerifyBankAccount(panditProfileId);
  const [vouching, setVouching] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const record = (id: number) =>
    verify.mutate(
      { id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: 'Check recorded — the account can be paid' });
          setVouching(null);
          setReason('');
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section title="Payout destinations" note="four digits and the IFSC — the number itself is never shown">
      {accounts.isPending && <LoadingState message="Reading the accounts…" />}
      {accounts.isError && <ErrorState title="Could not read the accounts" cause={errorMessage(accounts.error)} />}
      {accounts.isSuccess && accounts.data.items.length === 0 && (
        <EmptyState inline title="No account on file" body="The pandit adds one from the app; nothing is paid until then." />
      )}
      {accounts.isSuccess && accounts.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'holder', label: 'Holder' },
            { key: 'dest', label: 'Account', mono: true },
            { key: 'state', label: 'Checked' },
            { key: 'act', label: '' },
          ]}
          rows={accounts.data.items.map((a) => ({
            id: a.id,
            holder: a.account_holder_name,
            dest: `••••${a.account_number_last4} · ${a.ifsc}${a.is_primary ? ' · primary' : ''}`,
            state: (
              <StatusChip
                tone={PAYABLE_TO.has(a.verification_status) ? 'success' : a.verification_status === 'failed' ? 'error' : 'warn'}
                label={a.verification_status.replace(/_/g, ' ')}
              />
            ),
            act: PAYABLE_TO.has(a.verification_status) ? null : (
              <ScopeAction scope={scope} need="finance" size="sm" variant="ghost" onClick={() => setVouching(a.id)}>
                Record a check
              </ScopeAction>
            ),
          }))}
        />
      )}
      {vouching !== null && (
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', padding: 'var(--sp-md) 0 0' }}>
          <Input
            label="How it was checked"
            placeholder="A cancelled cheque on the KYC call"
            value={reason}
            onChange={setReason}
            style={{ flex: 1 }}
          />
          <ScopeAction
            scope={scope}
            need="finance"
            size="sm"
            variant="secondary"
            disabled={reason.trim() === '' || verify.isPending}
            onClick={() => record(vouching)}
          >
            Mark checked
          </ScopeAction>
          <Button variant="secondary" size="sm" onClick={() => setVouching(null)}>
            Cancel
          </Button>
        </div>
      )}
    </Section>
  );
}

/** The shared panel (doc 03 §6.15); kept under this name for the finance screens that embed it. */
export { AuditTrail };
